'use client';

import { useState } from 'react';

export interface FileItemEditable {
  id: string;
  filename?: string | null;
  url: string;
  mimeType?: string | null;
  createdAt: Date | string;
}

export interface FilesDisplayEditableProps {
  files: FileItemEditable[];
  emptyMessage?: string;
  t: (key: string) => string;
  className?: string;
  onDelete?: (fileId: string) => Promise<void>;
  isDeleting?: boolean;
}

export function FilesDisplayEditable({
  files,
  emptyMessage,
  className = '',
  t,
  onDelete,
  isDeleting = false,
}: FilesDisplayEditableProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDeleteClick = (fileId: string) => {
    setConfirmDeleteId(fileId);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete || !confirmDeleteId) return;

    setDeletingId(confirmDeleteId);
    setConfirmDeleteId(null);
    try {
      await onDelete(confirmDeleteId);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteId(null);
  };

  if (files.length === 0) {
    return (
      <div
        className={`flex justify-center items-center bg-el-grey-100 rounded-4 p-2 min-h-14 ${className}`}
      >
        <p className="text-12 text-el-grey-600">{emptyMessage || '-'}</p>
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-1 gap-2 bg-el-grey-100 rounded-4 p-2 ${className}`}
    >
      {files.map((file) => (
        <div
          key={file.id}
          className="flex items-center justify-between p-3 bg-el-grey-200 rounded-4"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="flex-shrink-0">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.53 9L13 3.47C12.8595 3.32931 12.6688 3.25018 12.47 3.25H8C7.27065 3.25 6.57118 3.53973 6.05546 4.05546C5.53973 4.57118 5.25 5.27065 5.25 6V18C5.25 18.7293 5.53973 19.4288 6.05546 19.9445C6.57118 20.4603 7.27065 20.75 8 20.75H16C16.7293 20.75 17.4288 20.4603 17.9445 19.9445C18.4603 19.4288 18.75 18.7293 18.75 18V9.5C18.7421 9.3116 18.6636 9.13309 18.53 9ZM13.25 5.81L16.19 8.75H13.25V5.81ZM16 19.25H8C7.66848 19.25 7.35054 19.1183 7.11612 18.8839C6.8817 18.6495 6.75 18.3315 6.75 18V6C6.75 5.66848 6.8817 5.35054 7.11612 5.11612C7.35054 4.8817 7.66848 4.75 8 4.75H11.75V9.5C11.7526 9.69811 11.8324 9.88737 11.9725 10.0275C12.1126 10.1676 12.3019 10.2474 12.5 10.25H17.25V18C17.25 18.3315 17.1183 18.6495 16.8839 18.8839C16.6495 19.1183 16.3315 19.25 16 19.25Z"
                  fill="currentcolor"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-12 font-medium text-el-grey-600 truncate">
                {file.filename || 'Unknown file'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Download button */}
            <a
              href={`/api/download/${file.id}`}
              download={file.filename || undefined}
              className="text-el-grey-600 hover:text-el-grey-800 transition-colors"
              title={t('actions.download') || 'Download'}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.3974 13.7998C18.5567 13.7999 18.7096 13.8631 18.8222 13.9756C18.9347 14.088 18.9979 14.2405 18.998 14.3994V16.7998C19.022 17.3561 18.8252 17.8995 18.4501 18.3115C18.0751 18.7235 17.5519 18.971 16.9951 19H7.02827C6.7506 18.9886 6.47769 18.9223 6.22554 18.8057C5.97334 18.6889 5.74675 18.5236 5.55855 18.3193C5.37037 18.1151 5.22436 17.8759 5.12886 17.6152C5.03335 17.3545 4.99009 17.0772 5.0019 16.7998V14.3994C5.00201 14.2405 5.06515 14.088 5.17769 13.9756C5.29033 13.8631 5.4432 13.7999 5.60249 13.7998C5.7618 13.7998 5.91462 13.8631 6.0273 13.9756C6.13989 14.088 6.20297 14.2404 6.20308 14.3994V16.7998C6.18241 17.0373 6.25421 17.2738 6.40327 17.46C6.55244 17.6461 6.76821 17.7678 7.00483 17.7998H16.9951C17.2316 17.7678 17.4465 17.646 17.5957 17.46C17.7448 17.2738 17.8165 17.0373 17.7959 16.7998V14.3994C17.796 14.2404 17.86 14.088 17.9726 13.9756C18.0853 13.8632 18.2381 13.7998 18.3974 13.7998ZM12.0117 5C12.171 5 12.3238 5.06334 12.4365 5.17578C12.5491 5.28821 12.6131 5.44062 12.6132 5.59961V12.9502L14.7919 10.7754C14.9057 10.6696 15.0564 10.6126 15.2119 10.6152C15.3675 10.618 15.5168 10.6801 15.6269 10.79C15.737 10.9 15.7999 11.0487 15.8027 11.2041C15.8054 11.3595 15.7477 11.5103 15.6416 11.624L12.4365 14.8242C12.3811 14.8801 12.3149 14.9239 12.2421 14.9541C12.1692 14.9843 12.0906 15.0004 12.0117 15C11.9329 15.0003 11.855 14.9843 11.7822 14.9541C11.7094 14.924 11.6432 14.8801 11.5878 14.8242L8.38276 11.624C8.27663 11.5103 8.21888 11.3595 8.22163 11.2041C8.22439 11.0487 8.28734 10.9 8.39741 10.79C8.5074 10.6803 8.65602 10.6181 8.81148 10.6152C8.96713 10.6125 9.11847 10.6694 9.23237 10.7754L11.4111 12.9512V5.59961C11.4112 5.44067 11.4743 5.2882 11.5869 5.17578C11.6995 5.06331 11.8524 5.00006 12.0117 5Z"
                  fill="currentcolor"
                />
              </svg>
            </a>
            {/* Delete button */}
            {onDelete && (
              <button
                type="button"
                onClick={() => handleDeleteClick(file.id)}
                disabled={isDeleting || deletingId === file.id}
                className="text-el-red-500 hover:text-el-red-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                title={t('actions.delete') || 'Delete'}
              >
                {deletingId === file.id ? (
                  <svg
                    className="animate-spin"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6H16V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V6H4C3.73478 6 3.48043 6.10536 3.29289 6.29289C3.10536 6.48043 3 6.73478 3 7C3 7.26522 3.10536 7.51957 3.29289 7.70711C3.48043 7.89464 3.73478 8 4 8H5V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V8H20C20.2652 8 20.5196 7.89464 20.7071 7.70711C20.8946 7.51957 21 7.26522 21 7C21 6.73478 20.8946 6.48043 20.7071 6.29289C20.5196 6.10536 20.2652 6 20 6ZM10 5H14V6H10V5ZM17 19H7V8H17V19Z"
                      fill="currentColor"
                    />
                    <path
                      d="M10 10C9.73478 10 9.48043 10.1054 9.29289 10.2929C9.10536 10.4804 9 10.7348 9 11V16C9 16.2652 9.10536 16.5196 9.29289 16.7071C9.48043 16.8946 9.73478 17 10 17C10.2652 17 10.5196 16.8946 10.7071 16.7071C10.8946 16.5196 11 16.2652 11 16V11C11 10.7348 10.8946 10.4804 10.7071 10.2929C10.5196 10.1054 10.2652 10 10 10Z"
                      fill="currentColor"
                    />
                    <path
                      d="M14 10C13.7348 10 13.4804 10.1054 13.2929 10.2929C13.1054 10.4804 13 10.7348 13 11V16C13 16.2652 13.1054 16.5196 13.2929 16.7071C13.4804 16.8946 13.7348 17 14 17C14.2652 17 14.5196 16.8946 14.7071 16.7071C14.8946 16.5196 15 16.2652 15 16V11C15 10.7348 14.8946 10.4804 14.7071 10.2929C14.5196 10.1054 14.2652 10 14 10Z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Confirmation Dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCancelDelete}
          />
          {/* Dialog */}
          <div className="relative bg-white rounded-8 p-6 shadow-lg max-w-sm w-full mx-4">
            <h3 className="text-16 font-semibold text-el-grey-800 mb-2">
              {t('actions.confirmDeleteTitle') || 'Delete file?'}
            </h3>
            <p className="text-14 text-el-grey-600 mb-6">
              {t('actions.confirmDeleteMessage') ||
                'Are you sure you want to delete this file? This action cannot be undone.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 text-14 text-el-grey-600 hover:text-el-grey-800 transition-colors cursor-pointer"
              >
                {t('actions.cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-14 bg-el-red-500 text-white rounded-4 hover:bg-el-red-600 transition-colors cursor-pointer"
              >
                {t('actions.delete') || 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
